import { OAUTH_ERROR_CODES, type OAuthErrorCode } from "@malfak/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter, useRouterState } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { apiKey } from "@/lib/api/hooks";
import { useT } from "@/lib/i18n";
import type { ChannelAccount } from "@/lib/types";

/** Search params the OAuth callback appends to `/settings/channels` (validated by the route). */
export interface OAuthReturnSearch {
  connected?: string;
  error?: string;
}

const KNOWN = new Set<string>(OAUTH_ERROR_CODES);
export const isOAuthErrorCode = (c: string): c is OAuthErrorCode => KNOWN.has(c);

/** Friendly message for a callback `?error=` code (unknown codes get a generic line naming the code). */
export function oauthErrorMessage(code: string, t: TFunction): string {
  const safe = code.replace(/[^a-z0-9_]/gi, "").slice(0, 40);
  return isOAuthErrorCode(safe) ? t(`oauth.returned.errors.${safe}`) : t("oauth.returned.errors.unknown", { code: safe || "?" });
}

/** Validate the channels route search (anything else is dropped). */
export function parseOAuthReturnSearch(raw: Record<string, unknown>): OAuthReturnSearch {
  const out: OAuthReturnSearch = {};
  if (typeof raw.connected === "string" && /^[0-9a-f-]{8,64}$/i.test(raw.connected)) out.connected = raw.connected;
  if (typeof raw.error === "string" && raw.error.length <= 60) out.error = raw.error;
  return out;
}

/**
 * Settings → Channels after the Instagram round trip: toast `?connected=<accountId>` (naming the account once the list
 * loads) or `?error=<code>`, then drop the params from the URL (replace, so Back doesn't toast again).
 */
export function useOAuthReturnToasts(accounts: ChannelAccount[] | undefined) {
  const { t } = useT();
  const router = useRouter();
  const qc = useQueryClient();
  const location = useRouterState({ select: (s) => s.location });
  const search = parseOAuthReturnSearch(location.search as Record<string, unknown>);
  const handled = useRef<string | null>(null);

  useEffect(() => {
    const key = search.connected ? `c:${search.connected}` : search.error ? `e:${search.error}` : null;
    if (!key || handled.current === key) return;
    if (search.connected && !accounts) return; // wait for the list to name the account
    handled.current = key;
    if (search.connected) {
      const acc = accounts?.find((a) => a.id === search.connected);
      toast.success(acc ? t("oauth.returned.connected", { user: acc.username }) : t("oauth.returned.connectedGeneric"), {
        description: t("oauth.returned.connectedHint"),
        id: `oauth-connected-${search.connected}`,
      });
      void qc.invalidateQueries({ queryKey: apiKey("accounts") });
      void qc.invalidateQueries({ queryKey: ["jobs"] });
    } else if (search.error) {
      toast.error(t("oauth.returned.failedTitle"), { description: oauthErrorMessage(search.error, t), id: `oauth-error-${search.error}`, duration: 12_000 });
    }
    const rest = { ...(location.search as Record<string, unknown>) };
    delete rest.connected;
    delete rest.error;
    void router.navigate({ to: location.pathname, search: rest as never, replace: true });
  }, [search.connected, search.error, accounts, t, qc, router, location.pathname, location.search]);
}
